import { useMemo, useState } from 'react';
import { Button } from './primitives/Button';
import { Card } from './primitives/Card';
import { IconButton } from './primitives/IconButton';

/* Action icons — 13px viewBox renders into the 24px IconButton. Stroke-only
 * for crispness; `currentColor` so muted/strong colors track the button's
 * own state. Kept inline to avoid pulling in an icon library for five glyphs. */
const ReplyIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" focusable="false">
    <path d="M6 4 L2 8 L6 12" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 8 H10 a 4 4 0 0 1 4 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" focusable="false">
    <path d="M10.5 2.5 L13.5 5.5 L5 14 H2 V11 Z" strokeLinejoin="round" />
    <line x1="9.5" y1="3.5" x2="12.5" y2="6.5" />
  </svg>
);
const DeleteIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" focusable="false">
    <path d="M2.5 4 H13.5" strokeLinecap="round" />
    <path d="M6 4 V2.5 H10 V4" strokeLinejoin="round" />
    <path d="M3.5 4 L4 14 H12 L12.5 4" strokeLinejoin="round" />
    <line x1="6.5" y1="7" x2="6.5" y2="11.5" />
    <line x1="9.5" y1="7" x2="9.5" y2="11.5" />
  </svg>
);
const ResolveIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" focusable="false">
    <polyline points="3,8.5 6.5,12 13,4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ReopenIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" focusable="false">
    <path d="M3 8 a 5 5 0 1 1 1.5 3.55" strokeLinecap="round" />
    <polyline points="3,5 3,8 6,8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CancelReplyIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true" focusable="false">
    <line x1="4" y1="4" x2="12" y2="12" strokeLinecap="round" />
    <line x1="12" y1="4" x2="4" y2="12" strokeLinecap="round" />
  </svg>
);

export type CommentsSubjectKind = 'document' | 'work_item';

export interface CommentsMember {
  id: string;
  name: string;
  handle?: string;
}

export interface CommentRow {
  id: string;
  parentCommentId: string | null;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: string;
  editedAt: string | null;
  resolvedAt: string | null;
}

export interface CommentsPanelProps {
  subjectKind: CommentsSubjectKind;
  subjectId: string;
  /** Flat list of comments. Top-level rows have parentCommentId === null. */
  comments: CommentRow[];
  /** Acting user — used for the 15-minute edit window + own-delete checks. */
  currentUserId: string;
  /** When true, delete is permitted on any comment. */
  isAdmin?: boolean;
  /** "Now" override (ms epoch) so the 15-min edit window is testable. */
  nowMs?: number;
  onAddComment: (input: {
    body: string;
    parentCommentId: string | null;
  }) => Promise<void> | void;
  onEditComment: (input: { id: string; body: string }) => Promise<void> | void;
  onDeleteComment: (id: string) => Promise<void> | void;
  onResolveComment: (id: string) => Promise<void> | void;
  onReopenComment: (id: string) => Promise<void> | void;
  /**
   * When true the panel renders only its inner list + composer — the parent
   * (typically `InspectorTabs`) supplies its own chrome (border, padding,
   * tab strip). When omitted/false the panel wraps itself in a `<Card>` with
   * a "Comments · N" head, matching the legacy single-column layout.
   */
  naked?: boolean;
}

const EDIT_WINDOW_MS = 15 * 60 * 1000;

function within15MinWindow(createdAt: string, nowMs: number): boolean {
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return false;
  return nowMs - t <= EDIT_WINDOW_MS;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

interface CommentInputProps {
  placeholder: string;
  submitLabel: string;
  initialBody?: string;
  testIdPrefix: string;
  onSubmit: (body: string) => Promise<void> | void;
  onCancel?: () => void;
}

function CommentInput({
  placeholder,
  submitLabel,
  initialBody = '',
  testIdPrefix,
  onSubmit,
  onCancel
}: CommentInputProps) {
  const [body, setBody] = useState(initialBody);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setBody('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pn-comments-input" data-testid={`${testIdPrefix}-wrap`}>
      <textarea
        className="pn-comments-input-textarea"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        rows={3}
        data-testid={`${testIdPrefix}-textarea`}
      />
      <div className="pn-comments-input-actions">
        {onCancel ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            data-testid={`${testIdPrefix}-cancel`}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={submitting || body.trim().length === 0}
          data-testid={`${testIdPrefix}-submit`}
        >
          {submitting ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </div>
  );
}

interface CommentItemProps {
  comment: CommentRow;
  replies?: CommentRow[];
  isTopLevel: boolean;
  isResolved: boolean;
  currentUserId: string;
  isAdmin: boolean;
  nowMs: number;
  collapsed?: boolean;
  onToggleResolved?: () => void;
  onSubmitReply?: (body: string) => Promise<void> | void;
  onEdit: (body: string) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
}

function CommentItem({
  comment,
  replies,
  isTopLevel,
  isResolved,
  currentUserId,
  isAdmin,
  nowMs,
  collapsed,
  onToggleResolved,
  onSubmitReply,
  onEdit,
  onDelete
}: CommentItemProps) {
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);

  const isAuthor = comment.authorId === currentUserId;
  const canEdit = isAuthor && within15MinWindow(comment.createdAt, nowMs);
  const canDelete = isAuthor || isAdmin;

  return (
    <li
      className="pn-comments-comment"
      data-testid={`comments-panel-comment-${comment.id}`}
      data-resolved={isResolved ? 'true' : 'false'}
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <header className="pn-comments-comment-header">
        <span className="pn-comments-comment-author">{comment.authorName}</span>
        <span className="pn-comments-comment-time">
          {formatTimestamp(comment.createdAt)}
        </span>
        {comment.editedAt ? (
          <span className="pn-comments-comment-edited">edited</span>
        ) : null}
      </header>

      {editing ? (
        <CommentInput
          placeholder="Edit comment"
          submitLabel="Save"
          initialBody={comment.body}
          testIdPrefix={`comments-panel-edit-${comment.id}`}
          onSubmit={async (body) => {
            await onEdit(body);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <p
          className="pn-comments-comment-body"
          data-testid={`comments-panel-body-${comment.id}`}
        >
          {comment.body}
        </p>
      )}

      <footer className="pn-comments-comment-actions">
        {isTopLevel && onSubmitReply ? (
          <IconButton
            onClick={() => setReplying((v) => !v)}
            aria-label={replying ? 'Cancel reply' : 'Reply'}
            title={replying ? 'Cancel reply' : 'Reply'}
            data-testid={`comments-panel-reply-toggle-${comment.id}`}
          >
            {replying ? <CancelReplyIcon /> : <ReplyIcon />}
          </IconButton>
        ) : null}
        {canEdit && !editing ? (
          <IconButton
            onClick={() => setEditing(true)}
            aria-label="Edit comment"
            title="Edit"
            data-testid={`comments-panel-edit-${comment.id}`}
          >
            <EditIcon />
          </IconButton>
        ) : null}
        {canDelete ? (
          <IconButton
            onClick={() => onDelete()}
            aria-label="Delete comment"
            title="Delete"
            data-testid={`comments-panel-delete-${comment.id}`}
          >
            <DeleteIcon />
          </IconButton>
        ) : null}
        {isTopLevel && onToggleResolved ? (
          <IconButton
            onClick={onToggleResolved}
            aria-label={isResolved ? 'Reopen thread' : 'Resolve thread'}
            title={isResolved ? 'Reopen' : 'Resolve'}
            data-testid={
              isResolved
                ? `comments-panel-reopen-${comment.id}`
                : `comments-panel-resolve-${comment.id}`
            }
          >
            {isResolved ? <ReopenIcon /> : <ResolveIcon />}
          </IconButton>
        ) : null}
      </footer>

      {isTopLevel && isResolved ? (
        <p
          className="pn-comments-resolved-banner"
          data-testid={`comments-panel-resolved-banner-${comment.id}`}
        >
          Resolved · click to reopen
        </p>
      ) : null}

      {isTopLevel && !collapsed ? (
        <>
          {replies && replies.length > 0 ? (
            <ul
              className="pn-comments-replies"
              data-testid={`comments-panel-replies-${comment.id}`}
            >
              {replies.map((r) => (
                <CommentItem
                  key={r.id}
                  comment={r}
                  isTopLevel={false}
                  isResolved={isResolved}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                  nowMs={nowMs}
                  onEdit={(body) => onEdit(body)}
                  onDelete={() => onDelete()}
                />
              ))}
            </ul>
          ) : null}
          {replying && onSubmitReply ? (
            <CommentInput
              placeholder="Write a reply…"
              submitLabel="Reply"
              testIdPrefix={`comments-panel-reply-${comment.id}`}
              onSubmit={async (body) => {
                await onSubmitReply(body);
                setReplying(false);
              }}
              onCancel={() => setReplying(false)}
            />
          ) : null}
        </>
      ) : null}
    </li>
  );
}

export function CommentsPanel({
  subjectKind,
  subjectId,
  comments,
  currentUserId,
  isAdmin = false,
  nowMs,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onResolveComment,
  onReopenComment,
  naked = false
}: CommentsPanelProps) {
  const now = nowMs ?? Date.now();
  const [collapsedThreads, setCollapsedThreads] = useState<
    Record<string, boolean>
  >(() => {
    const init: Record<string, boolean> = {};
    comments.forEach((c) => {
      if (c.parentCommentId === null && c.resolvedAt !== null) {
        init[c.id] = true;
      }
    });
    return init;
  });

  const topLevel = useMemo(
    () => comments.filter((c) => c.parentCommentId === null),
    [comments]
  );

  const repliesByParent = useMemo(() => {
    const map = new Map<string, CommentRow[]>();
    comments.forEach((c) => {
      if (c.parentCommentId !== null) {
        const list = map.get(c.parentCommentId) ?? [];
        list.push(c);
        map.set(c.parentCommentId, list);
      }
    });
    return map;
  }, [comments]);

  const body = (
    <>
      {topLevel.length === 0 ? (
        <p
          className="pn-comments-panel-empty"
          data-testid="comments-panel-empty"
        >
          No comments yet.
        </p>
      ) : (
        <ul className="pn-comments-panel-list">
          {topLevel.map((c) => {
            const resolved = c.resolvedAt !== null;
            const collapsed = resolved && collapsedThreads[c.id] !== false;
            return (
              <CommentItem
                key={c.id}
                comment={c}
                replies={repliesByParent.get(c.id) ?? []}
                isTopLevel
                isResolved={resolved}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                nowMs={now}
                collapsed={collapsed}
                onToggleResolved={async () => {
                  if (resolved) {
                    await onReopenComment(c.id);
                    setCollapsedThreads((prev) => ({ ...prev, [c.id]: false }));
                  } else {
                    await onResolveComment(c.id);
                    setCollapsedThreads((prev) => ({ ...prev, [c.id]: true }));
                  }
                }}
                onSubmitReply={(body) =>
                  onAddComment({ body, parentCommentId: c.id })
                }
                onEdit={(body) => onEditComment({ id: c.id, body })}
                onDelete={() => onDeleteComment(c.id)}
              />
            );
          })}
        </ul>
      )}

      <CommentInput
        placeholder="Write a comment…"
        submitLabel="Comment"
        testIdPrefix="comments-panel-new"
        onSubmit={(body) => onAddComment({ body, parentCommentId: null })}
      />
    </>
  );

  if (naked) {
    return (
      <div
        className="pn-comments-panel-naked"
        data-testid="comments-panel"
        data-subject-kind={subjectKind}
        data-subject-id={subjectId}
        aria-label="Comments"
      >
        {body}
      </div>
    );
  }

  return (
    <Card
      data-testid="comments-panel"
      data-subject-kind={subjectKind}
      data-subject-id={subjectId}
      aria-label="Comments"
    >
      <Card.Head title="Comments" count={topLevel.length} />
      <Card.Body>{body}</Card.Body>
    </Card>
  );
}
